<template>
    <!-- top-right matches where vue3-toastify put notifications before the
         re-skin; Nuxt UI defaults to the bottom, where they collide with the
         footer and are easy to miss on data-heavy pages. -->
    <UApp :locale="uiLocale" :toaster="{ position: 'top-right' }">
        <LoaderComponent v-if="appStore.isLoaderActive()" />
        <component :is="layout">
            <RouterView />
        </component>
    </UApp>
</template>
<script lang="ts" setup>
    import * as locales from '@nuxt/ui/locale'
    import { computed, onMounted, onBeforeUnmount, ref, type Ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { RouterView, useRoute } from 'vue-router'

    import LoaderComponent from '@/components/LoaderComponent.vue'
    import Blank from '@/layouts/Blank.vue'
    import Default from '@/layouts/Default.vue'
    import router from '@/router'
    import { useAppStore } from '@/stores/app'
    import { useBulletinStore } from '@/stores/bulletin'
    import { useNotificationStore } from '@/stores/notification'
    import type { Unit } from '@/types/Unit'
    import type { UserData } from '@/types/User'

    // data
    const appStore = useAppStore()
    const route = useRoute()
    const { locale } = useI18n()

    // Keep Nuxt UI's built-in component texts in sync with the vue-i18n locale.
    // Fall back to English if the locale is not shipped by @nuxt/ui/locale.
    const uiLocale = computed(() => (locales as unknown as Record<string, typeof locales.en>)[locale.value] ?? locales.en)

    const layouts = { Default, Blank }
    const layout = computed(() => layouts[(route.meta.layout as keyof typeof layouts) ?? 'Default'])
    const notificationStore = useNotificationStore()
    const bulletinStore = useBulletinStore()
    const unreadItemsInterval: Ref<number | null> = ref(null)
    const pagesInterval: Ref<number | null> = ref(null)
    const firstLoad: Ref<boolean> = ref(true)

    // methods
    const onWindowResized = () => {
        appStore.resizeHandler()
    }

    const setUnit = () => {
        const unit = localStorage.getItem('selectedUnit')
        if (unit && unit !== 'undefined' && typeof unit === 'string') {
            appStore.setSelectedUnit(JSON.parse(unit) as Unit)
        }
    }

    const getUnreadNumbers = () => {
        notificationStore.updateUnreadNotifications()
        bulletinStore.updateUnreadBulletins()
    }

    const rerouteAllowed = () => {
        const allowedPages = appStore.getAllowedPages()
        if (!allowedPages.includes(Number(route.meta.page)) && route.meta.requiresAuth) {
            router.push({ name: 'not-found' })
        }
    }

    /**
     * localStorage is attacker- and bug-writable: it can hold malformed JSON, or
     * the literal `null`. An uncaught throw here aborts the rest of onMounted,
     * and a null user object crashes every consumer that reads a name off it.
     */
    const setUserDataFromLocalStorage = () => {
        const userDataString = localStorage.getItem('userData')
        if (!userDataString || userDataString === 'undefined') return

        try {
            const parsed = JSON.parse(userDataString)
            if (parsed && typeof parsed === 'object') appStore.setUserData(parsed as UserData)
        } catch {
            localStorage.removeItem('userData')
        }
    }

    // hooks
    onMounted(() => {
        if (!localStorage.getItem('allowedPages')) appStore.setInternalPages()
        setUserDataFromLocalStorage()
        setUnit()
        onWindowResized()
        window.addEventListener('resize', onWindowResized)
        appStore.resizeHandler()

        pagesInterval.value = window.setInterval(() => {
            appStore.setAllowedPages()
            rerouteAllowed()
        }, 15000)

        unreadItemsInterval.value = window.setInterval(() => {
            getUnreadNumbers()
        }, 60000)

        setTimeout(() => {
            const element = document.getElementById('loading-bg')
            if (element) element.style.display = 'none'
        }, 50)
    })

    onBeforeUnmount(() => {
        window.removeEventListener('resize', onWindowResized)
        if (unreadItemsInterval.value) window.clearInterval(unreadItemsInterval.value)
    })

    router.beforeEach(async(to) => {
        if (firstLoad.value) {
            if (to.name === 'login-as-user') {
                appStore.setLogout()
            } else {
                await appStore.setAllowedPagesAsync()
                getUnreadNumbers()
            }
            firstLoad.value = false
        }
    })
</script>
